import { z } from 'zod';
import { passwordSchema } from './auth.js';
// =============================================================================
// Admin — Users (M1.10)
// =============================================================================
export const userStatusSchema = z.enum(['ACTIVE', 'INACTIVE', 'BLOCKED', 'PENDING_VERIFICATION']);
export const listUsersQuerySchema = z.object({
    q: z.string().optional(),
    status: userStatusSchema.optional(),
    role: z.string().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const createUserInputSchema = z.object({
    email: z.string().email().toLowerCase(),
    fullName: z.string().min(2).max(120),
    password: passwordSchema,
    roles: z.array(z.string()).default([]),
    emailVerified: z.boolean().default(true),
});
export const updateUserInputSchema = z.object({
    fullName: z.string().min(2).max(120).optional(),
    status: userStatusSchema.optional(),
    roles: z.array(z.string()).optional(),
});
// =============================================================================
// Admin — Audit log
// =============================================================================
export const listAuditLogsQuerySchema = z.object({
    actorUserId: z.string().optional(),
    entity: z.string().optional(),
    entityId: z.string().optional(),
    action: z.string().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
// =============================================================================
// Admin — Coupons (M2.7)
// =============================================================================
export const discountTypeSchema = z.enum(['PERCENT', 'FIXED']);
export const listCouponsQuerySchema = z.object({
    q: z.string().optional(),
    active: z.enum(['true', 'false']).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const createCouponInputSchema = z
    .object({
    code: z
        .string()
        .min(3)
        .max(40)
        .regex(/^[A-Z0-9_-]+$/i, 'Use letras, números, _ e -')
        .transform((s) => s.toUpperCase()),
    discountType: discountTypeSchema,
    discountValue: z.coerce.number().int().positive(),
    validFrom: z.string().datetime().optional().nullable(),
    validUntil: z.string().datetime().optional().nullable(),
    maxUses: z.coerce.number().int().positive().optional().nullable(),
    marketCode: z.enum(['B3', 'FOREX']).optional().nullable(),
    active: z.boolean().default(true),
})
    .refine((v) => (v.discountType === 'PERCENT' ? v.discountValue >= 1 && v.discountValue <= 100 : true), { message: 'Percentual deve estar entre 1 e 100', path: ['discountValue'] });
export const updateCouponInputSchema = z.object({
    validUntil: z.string().datetime().optional().nullable(),
    maxUses: z.coerce.number().int().positive().optional().nullable(),
    active: z.boolean().optional(),
});
// =============================================================================
// Admin — Terms of Use (M2.8)
// =============================================================================
export const publishTermsInputSchema = z.object({
    version: z
        .string()
        .min(3)
        .max(40)
        .regex(/^[\w.-]+$/, 'Use letras, números, ponto, _, -'),
    marketCode: z.enum(['B3', 'FOREX']).optional().nullable(),
    contentMd: z.string().min(50, 'Conteúdo dos termos muito curto'),
});
// =============================================================================
// Admin — Contas Virtuais (M3 — inspecionar / recalcular / intervir)
// =============================================================================
export const accountPhaseKindSchema = z.enum(['TEST', 'FUNDED', 'LIVE']);
export const accountStatusSchema = z.enum([
    'TEST',
    'FUNDED',
    'LIVE',
    'BLOWN',
    'RESTARTED',
    'CLOSED',
]);
export const listAccountsQuerySchema = z.object({
    phase: accountPhaseKindSchema.optional(),
    status: accountStatusSchema.optional(),
    traderId: z.string().optional(),
    marketCode: z.enum(['B3', 'FOREX']).optional(),
    q: z.string().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const adjustAccountSchema = z.object({
    zeroPointCents: z.number().int().optional(),
    currentBalanceCents: z.number().int().optional(),
    reason: z.string().min(3, 'Informe o motivo do ajuste'),
});
export const resetAccountSchema = z.object({
    newZeroPointCents: z.number().int().positive().optional(),
    reason: z.string().min(3, 'Informe o motivo do reset'),
});
/** Marcar conta como "Não aprovado" (blow manual) com motivo opcional. */
export const markNotApprovedSchema = z.object({
    note: z.string().max(500).optional(),
});
/** Ligar/desligar a oferta de restart no dashboard do trader. */
export const setRestartOfferSchema = z.object({
    enabled: z.boolean(),
});
/** Liberar/revogar o restart incluso (self-service) da conta reprovada. */
export const setRestartAvailableSchema = z.object({
    enabled: z.boolean(),
});
// =============================================================================
// Admin — Total de repasses aos traders (saques confirmados, líquido, por período)
// =============================================================================
export const payoutsTotalQuerySchema = z.object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
});
// =============================================================================
// Admin — Receita no período (faturas pagas, Stripe + Mercado Pago, por mercado)
// =============================================================================
export const revenueTotalQuerySchema = z.object({
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
});
/** Cria assinatura ACTIVE manual (sem pagamento) e já provisiona a conta virtual. */
export const manualActivationSchema = z.object({
    userId: z.string().min(1, 'Selecione o trader'),
    planId: z.string().min(1, 'Selecione o plano'),
    subAccountNelogicaId: z.string().min(1, 'Informe o ID da subconta'),
    zeroPointCents: z.coerce.number().int().positive().optional(),
    accountLabel: z.string().max(120).optional(),
    notes: z.string().max(2048).optional(),
});
// =============================================================================
// Admin — Configurações de campanha (GlobalSetting booleanos)
// =============================================================================
/** Chaves de campanha que o admin liga/desliga na tela de Configurações. */
export const campaignSettingKeySchema = z.enum(['campaign.restart_included', 'campaign.buy1get2']);
export const setCampaignSettingSchema = z.object({
    key: campaignSettingKeySchema,
    enabled: z.boolean(),
});
/** Salvar preços de restart. priceCents null limpa o preço do plano (cai no genérico). */
export const setRestartPricesSchema = z.object({
    items: z.array(z.object({
        code: z.string().min(1),
        priceCents: z.number().int().positive().nullable(),
    })),
    genericPriceCents: z.number().int().positive().nullable().optional(),
});
export const setExcellenceSettingsSchema = z.object({
    enabled: z.boolean(),
    url: z
        .string()
        .trim()
        .max(2000)
        .nullable()
        .refine((v) => !v || /^https?:\/\//.test(v), 'A URL deve começar com http:// ou https://'),
});
/** Texto padrão do banner de "novo teste" (usado quando o admin não personalizou). */
export const DEFAULT_NEW_TEST_BANNER_TEXT = 'Até os melhores traders tomam stop. Use o cupom [CUPOM] para iniciar o seu novo Challenge!';
export const setNewTestBannerSchema = z.object({
    text: z.string().trim().min(1).max(400),
    couponCode: z
        .string()
        .trim()
        .max(40)
        .nullable()
        .transform((v) => (v && v.length > 0 ? v.toUpperCase() : null)),
});
export const setCheckoutFeeSettingsSchema = z.object({
    // Percentual pode ter casas decimais (4.69). Máx 30: acima disso é
    // certamente erro de digitação (repasse de taxa, não margem).
    cardFeePercent: z.number().min(0).max(30),
    cardFeeFixedCents: z.number().int().min(0).max(10_000),
});
export const setReferralSettingsSchema = z.object({
    enabled: z.boolean(),
    creditB3Cents: z.number().int().min(0).max(100_000_00),
    creditForexUsdCents: z.number().int().min(0).max(100_000_00),
    // Opcional: SPA antiga em cache (sem o campo) não pode quebrar o PUT — o
    // backend mantém o valor atual quando ausente. Máx 99: 100% zera a fatura e
    // quebra a Subscription incomplete do Stripe (sem PaymentIntent).
    discountPercent: z.number().int().min(0).max(99).optional(),
});
/** PATCH /admin/referrals/coupons/:id — ajusta o % de um cupom de indicação. */
export const updateReferralCouponSchema = z.object({
    // Máx 99: desconto de 100% zera a fatura e quebra o fluxo de pagamento.
    discountValue: z.number().int().min(0).max(99),
});
// =============================================================================
// Janelas de operação (M5) — horário permitido por mercado (B3 / Forex)
// =============================================================================
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
/** Valida que a string é uma timezone IANA real (senão o Intl lança em runtime). */
function isValidTimezone(tz) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}
export const operatingWindowSchema = z.object({
    weekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7),
    open: z.string().regex(HHMM, 'Use HH:mm (00:00–23:59)'),
    close: z.string().regex(HHMM, 'Use HH:mm (00:00–23:59)'),
    timezone: z
        .string()
        .min(1)
        .max(64)
        .refine(isValidTimezone, 'Timezone inválida (use IANA, ex.: America/Sao_Paulo)'),
});
export const setOperatingWindowsSchema = z.object({
    b3: operatingWindowSchema,
    forex: operatingWindowSchema,
});
/** Disparar todos os templates de email de teste para um endereço (validação). */
export const sendTestEmailsSchema = z.object({
    to: z.string().email(),
});
/** Definir/editar a mensagem de motivo customizada (null limpa). */
export const setClosureNoteSchema = z.object({
    note: z.string().max(500).nullable(),
});
/** Editar o número/ID da subconta na Nelogica (pode mudar após o restart). */
export const setAccountNelogicaIdSchema = z.object({
    subAccountNelogicaId: z.string().trim().min(1, 'Informe o número da conta').max(64),
});
export const listWithdrawalsQuerySchema = z.object({
    status: z.enum(['REQUESTED', 'APPROVED', 'REJECTED', 'PAID', 'CANCELED']).optional(),
});
export const rejectWithdrawalSchema = z.object({
    reason: z.string().min(3, 'Informe o motivo da rejeição'),
});
/** Admin define uma nova senha para qualquer usuário. */
export const adminResetPasswordSchema = z.object({
    newPassword: z.string().min(8, 'A senha deve ter ao menos 8 caracteres'),
});
// =============================================================================
// Admin — Newsletter / Comunicados (envio editorial pra base de emails)
// =============================================================================
export const newsletterStatusSchema = z.enum(['DRAFT', 'SCHEDULED', 'SENDING', 'SENT', 'FAILED']);
export const listNewslettersQuerySchema = z.object({
    status: newsletterStatusSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const upsertNewsletterSchema = z.object({
    subject: z.string().trim().min(3, 'Assunto muito curto').max(200),
    contentHtml: z.string().trim().min(1, 'Escreva o conteúdo do comunicado').max(100_000),
});
/** Envio de teste pra um único email (não toca na base). */
export const previewNewsletterSchema = z.object({
    to: z.string().email('Email inválido'),
});
/** Enviar agora (sem scheduledFor) ou agendar (com scheduledFor no futuro). */
export const sendNewsletterSchema = z.object({
    scheduledFor: z.string().datetime().optional().nullable(),
});
const bannerConfigSchema = z.object({
    enabled: z.boolean(),
    text: z.string().trim().max(280),
    buttonText: z.string().trim().max(40),
    buttonUrl: z
        .string()
        .trim()
        .max(2000)
        .nullable()
        .refine((v) => !v || /^https?:\/\//.test(v), 'A URL deve começar com http:// ou https://'),
});
export const setBannerSettingsSchema = z.object({
    top: bannerConfigSchema,
    promo: bannerConfigSchema,
});
// =============================================================================
// Admin — Programa de Afiliados (dinheiro real; permissions affiliates:read/write)
// =============================================================================
export const affiliateStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
/** POST /admin/affiliates — cadastro manual pelo admin (aprovação manual). */
export const createAffiliateSchema = z.object({
    name: z.string().trim().min(2).max(120),
    email: z.string().email().toLowerCase(),
    /**
     * CPF OBRIGATÓRIO (validado no backend): o bloqueio de auto-compra
     * "CPF do comprador != CPF do afiliado" depende dele existir.
     */
    cpf: z.string().trim().min(11).max(20),
    couponCode: z
        .string()
        .trim()
        .min(3)
        .max(40)
        .regex(/^[A-Z0-9_-]+$/i, 'Use letras, números, _ e -')
        .transform((s) => s.toUpperCase()),
    commissionPercent: z.number().int().min(0).max(100).default(10),
    // Máx 99: desconto de 100% zera a cobrança e quebra o fluxo de pagamento.
    couponDiscountPercent: z.number().int().min(0).max(99).default(50),
});
/** PATCH /admin/affiliates/:id — %s, status e CPF (couponDiscountPercent atualiza o Coupon). */
export const updateAffiliateSchema = z.object({
    commissionPercent: z.number().int().min(0).max(100).optional(),
    couponDiscountPercent: z.number().int().min(0).max(99).optional(),
    status: affiliateStatusSchema.optional(),
    /** Corrige/complementa o CPF do afiliado (validado no backend). */
    cpf: z.string().trim().min(11).max(20).optional(),
});
//# sourceMappingURL=admin.js.map